import React from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
// components
import { ReusableMap } from "app/static/coffee/reusable_map"
import "app/static/stylus/reusable_map.styl"
import "sheets/components/stylus/SheetsMapWrapper.styl"

import sheetConstants from "sheets/constants"

/**
 * Component to render the reusable_map
 */
export default class SheetsMapWrapper extends React.Component {
    constructor(props) {
        super(props)
        this.setMapData = this.setMapData.bind(this)
        this.handleMapClick = this.handleMapClick.bind(this)
        this.map = undefined

        // set up the abbreviation mapping for later lookup
        // generate a mapping of abbreviation => region enum value
        this.stateMapping = Object.entries(DjangIO.app.models.Region._items)
            .filter((intermediate_region_array) => intermediate_region_array[1].is_state_region)
            .reduce((acc, intermediate_region_array) => {
                acc[intermediate_region_array[1].abbrev] = intermediate_region_array[1].value
                return acc
            }, {})
    }

    // when the component mounts, we want to initialize the map and set its map data
    componentDidMount() {
        this.map = this.initializeMap()
        this.setMapData(this.props.mapData)
    }

    // when the component receives props, we want to set the counts for each state
    UNSAFE_componentWillReceiveProps(nextProps) {
        // pass new data to the map
        this.setMapData(nextProps.mapData, nextProps.quorumDataType)
    }

    /**
     * Initialize the map. ReusableMap returns a function that is then called to
     * initialize the map
     */
    initializeMap() {
        window.state_click_callback = this.handleMapClick

        // give the map colors, if we were able to get them in the view template
        // from the organization design
        // otherwise, the map will default to the Quorum colors
        const colorConfig = {}
        if (window.primaryColor && window.secondaryColor) {
            colorConfig.primaryColor = window.primaryColor
            colorConfig.secondaryColor = window.secondaryColor

            // if they don't include this, it will just default to Quorum purple
            colorConfig.darkerPrimaryColor = window.darkerPrimaryColor
        }

        const mapInstance = new ReusableMap(this.props.quorumDataType, colorConfig)
        const margin = {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        }
        let width = Math.min(sheetConstants.MAP_WIDTH, (document.querySelector("#container").offsetWidth * 2) / 3)
        // initialize
        mapInstance(this.props.mapTarget, margin, width, (4 * width) / 10)

        return mapInstance
    }

    /** set the data of the map
     * @param {Object} mapData - the data for the map
     * the Reusable Map component wants the mapData argument to update_data in the following form:
     * [
     * ...
     * {state: "al", value: 0}
     * {state: "ak", value: 0}
     * {state: "az", value: 0}
     * {state: "ar", value: 0}
     * ....
     * ]
     * mapData should be an array with an element for each state, where each element is an object
     * with a state key and a value key
     */
    setMapData(mapData, quorumDataType) {
        // make sure there is mapData and it's not empty
        if (mapData && mapData.length) {
            this.map.update_data(mapData, quorumDataType)
        }
    }

    // handle the click event on the map
    // we pass this function to the event listener as a callback to get executed upon clicking the state
    handleMapClick(e) {
        // classList either has a length of 2 or 3, depending on if we are clicking a state or unclicking a state
        // when we click on a state, the length will be 3. When CA is clicked, here's what it looks like:
        // ["state, "CA", "clicked"]
        // we can pull the state abbrevaition out as well as whether we are clicking/unclicking from here
        const classNameShim = e.currentTarget.className.baseVal.split(" ")
        const stateAbbrev = classNameShim[1].toLowerCase()
        // call the action creator
        if (classNameShim.length === 3) {
            this.props.addMapFilter(this.props.sheetId, this.stateMapping[stateAbbrev])
        } else if (classNameShim.length === 2) {
            this.props.removeMapFilter(this.props.sheetId, this.stateMapping[stateAbbrev])
        } else {
            throw new Error("the map does not follow the above invariant for some reason")
        }
    }

    render() {
        const { show_map } = this.props
        // render the target for the map
        return (
            <div className="public-sheets-map-wrapper">
                <div
                    className={classNames({ hidden: !show_map })}
                    data-cy="public-sheet-map-target"
                    id={this.props.mapTarget}
                />
                <div className="legend-for-map" data-cy="legend-for-map">
                    <div
                        id="legend-label"
                        className={classNames({ hidden: !show_map, "general-map-legend": show_map })}
                    />
                    <div
                        id="legend-space-adv-src"
                        className={classNames({ hidden: !show_map, "general-map-legend": show_map })}
                    />
                </div>
            </div>
        )
    }
}

SheetsMapWrapper.propTypes = {
    mapTarget: PropTypes.string,
    show_map: PropTypes.bool,
    quorumDataType: PropTypes.number.isRequired,
    mapData: PropTypes.array,
}

SheetsMapWrapper.defaultProps = {
    quorumDataType: DjangIO.app.models.QuorumDataType.bill.value,
    mapTarget: "public-sheets-map-target",
    mapData: [],
    show_map: false,
}
